import { useMemo, useRef } from "react";

import { ChatOnFinishCallback, isStaticToolUIPart, UIMessage } from "ai";
import { useSearchParams } from "next/navigation";

import { buildOrganizationAssistantPage } from "./buildOrganizationAssistantPage";
import { ORGANIZATION_ASSISTANT_CONFIG } from "./organizationAssistantConfig";
import { createOrganizationAssistantTransport } from "./organizationAssistantTransport";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { trpc } from "@/common/helpers/trpc";
import { useChatWidgetRoute } from "@/components/ChatWidgetVisibilityManager/helpers/useChatWidgetRoute";
import type { AssistantChatInitial } from "@/features/Assistant/helpers/types";
import { useAssistantChat } from "@/features/Assistant/helpers/useAssistantChat";

const ORGANIZATION_ASSISTANT_DISABLED = "organization_assistant_disabled";

export const useOrganizationAssistant = (initial?: AssistantChatInitial) => {
  const utils = trpc.useUtils();
  const aiSessionId = initial?.aiSessionId;
  const onSessionCreated = initial?.onSessionCreated;
  const { organization } = useCurrentUserContext();
  const organizationId = organization?.id ?? "";
  const { locale } = useLocaleContext();
  const { pathWithoutLocale } = useChatWidgetRoute();
  const searchParams = useSearchParams();
  const streamContextRef = useRef({ locale, pathWithoutLocale, searchParams });
  streamContextRef.current = { locale, pathWithoutLocale, searchParams };
  const transport = useMemo(
    () =>
      createOrganizationAssistantTransport(utils.client, {
        organizationId,
        aiSessionId,
        onSessionCreated,
        getStreamContext: () => ({
          locale: streamContextRef.current.locale,
          page: buildOrganizationAssistantPage(
            streamContextRef.current.pathWithoutLocale,
            streamContextRef.current.searchParams
          ),
        }),
      }),
    [utils.client, organizationId, aiSessionId, onSessionCreated]
  );
  const refreshedSalesCallIds = useRef(new Set<string>());

  const onFinish: ChatOnFinishCallback<UIMessage> = ({ message }) => {
    message.parts.filter(isStaticToolUIPart).forEach((part) => {
      const formId = part.type === "tool-setSalesOpen" ? getChangedSalesFormId(part) : null;
      if (!formId || refreshedSalesCallIds.current.has(part.toolCallId)) {
        return;
      }
      refreshedSalesCallIds.current.add(part.toolCallId);
      void Promise.all([
        utils.getCampaignListPagedTableData.invalidate(),
        utils.getCampaignHubPageData.invalidate({ formId }),
        utils.getCampaignOverviewPageData.invalidate({ formId }),
      ]);
    });
  };

  const onError = (error: Error) => {
    if (error.message === ORGANIZATION_ASSISTANT_DISABLED) {
      void utils.getIsOrganizationAssistantEnabled.invalidate();
    }
  };

  return useAssistantChat({
    transport,
    initial,
    config: ORGANIZATION_ASSISTANT_CONFIG,
    onFinish,
    onError,
    onToolRendered: initial?.onToolRendered,
  });
};

export type OrganizationAssistantChat = ReturnType<typeof useOrganizationAssistant>;

const getChangedSalesFormId = (part: { state: string; input?: unknown; output?: unknown }): string | null => {
  if (part.state !== "output-available" || !hasChangedSales(part.output)) {
    return null;
  }
  const ticketingId = getTicketingId(part.input);
  return ticketingId ? ticketingId.trim().toLowerCase() : null;
};

const hasChangedSales = (output: unknown): boolean =>
  typeof output === "object" &&
  output !== null &&
  "status" in output &&
  (output.status === "closed" || output.status === "opened");

const getTicketingId = (input: unknown): string | null =>
  typeof input === "object" && input !== null && "ticketingId" in input && typeof input.ticketingId === "string"
    ? input.ticketingId
    : null;
